import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
  Turnstile,
  type TurnstileInstance,
} from '@marsidev/react-turnstile';
import { Link } from 'react-router-dom';
import './index.css';
import serviceIcon3dPrint from '../../assets/images/service-icon-3dprint.webp';
import serviceIconApps from '../../assets/images/service-icon-apps.webp';
import serviceIconConsulting from '../../assets/images/service-icon-consulting.webp';
import serviceIconWebsite from '../../assets/images/service-icon-website.webp';

import letsconnectimage from '../../assets/images/letsconnect.webp'

const SERVICE_ITEMS = [
  { label: 'Custom 3D Prints', icon: serviceIcon3dPrint, path: '/services/3dprints' },
  { label: 'Websites', icon: serviceIconWebsite, path: '/services/websites' },
  { label: 'App Development', icon: serviceIconApps, path: '/services/appdev' },
  { label: 'Tech Consulting', icon: serviceIconConsulting, path: '/services/consulting' },
];

const Contact = () => {
  const post_url = process.env.REACT_APP_MAIL_FUNCTION_URL || '';
  const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
  const hogcloudContactCardUrl = `${process.env.PUBLIC_URL}/hogcloud-contact.vcf`;

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [botStatus, setBotStatus] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [useCompactTurnstile, setUseCompactTurnstile] = useState(false);
  const turnstileRef = useRef<TurnstileInstance | null>(null);

  const [formData, setFormData] = useState({
    fullname: '',
    email: '',
    custom_message: '',
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      setIsSubmitted(true);
    }

    if (!window.matchMedia) {
      return;
    }

    const compactQuery = window.matchMedia('(max-width: 480px)');
    const updateTurnstileSize = () => setUseCompactTurnstile(compactQuery.matches);
    updateTurnstileSize();
    compactQuery.addEventListener?.('change', updateTurnstileSize);

    return () => compactQuery.removeEventListener?.('change', updateTurnstileSize);
  }, []);

  const formReady = useMemo(() => {
    return (
      formData.fullname.trim().length > 0 &&
      formData.email.trim().length > 0 &&
      turnstileToken.length > 0 &&
      !isSubmitting
    );
  }, [formData.fullname, formData.email, isSubmitting, turnstileToken]);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!formData.fullname.trim() || !formData.email.trim()) {
      setBotStatus('Please enter your name and email.');
      return;
    }

    if (!turnstileSiteKey) {
      setBotStatus('Human verification is not configured.');
      return;
    }

    if (!turnstileToken) {
      setBotStatus('Please complete the human verification.');
      return;
    }

    if (!post_url) {
      setBotStatus('Contact service is not configured.');
      return;
    }

    const form = document.forms.namedItem('contact') as HTMLFormElement | null;
    if (!form) {
      setBotStatus('Form not found.');
      return;
    }

    try {
      setIsSubmitting(true);
      setBotStatus('Sending ...');

      const submittedFormData = new FormData(form);

      const formObject: Record<string, any> = {};
      submittedFormData.forEach((value, key) => {
        if (key === 'requested_services') {
          if (!formObject[key]) formObject[key] = [];
          formObject[key].push(value);
        } else {
          formObject[key] = value;
        }
      });
      formObject.turnstile_token = turnstileToken;

      const response = await fetch(post_url, {
        method: 'POST',
        body: JSON.stringify(formObject),
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json().catch(() => null);

      if (response.ok) {
        setIsSubmitted(true);
        window.location.href = '/contact?success=true';
      } else {
        setTurnstileToken('');
        turnstileRef.current?.reset();
        setBotStatus(result?.message || 'Verification failed. Please try again.');
      }
    } catch (error) {
      setTurnstileToken('');
      turnstileRef.current?.reset();
      setBotStatus('Unable to send your message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      {isSubmitted ? (
        <div className="success-card">
          <h1>Message received!</h1>
          <p>Thanks for reaching out. We will contact you soon.</p>
        </div>
      ) : (
        <div className="contact-shell">
                        <section className="contact-download-card" aria-label="Add Hogcloud contact information">
                  <div className="contact-download-copy">
                    <h2>Add Hogcloud VCard to your Contacts</h2>
                  </div>
                  <a
                    href={hogcloudContactCardUrl}
                    download="hogcloud-contact.vcf"
                    className="contact-download-link"
                  >
                    Add Contact Card
                  </a>
                </section>
          <section className="contact-hero">
                <h1>Contact Us</h1>
          </section>
          <form name="contact" id="contact" className="contact-card" onSubmit={handleSubmit}>
            <div className="fields-grid">
              <div className="field">
                <label htmlFor="name">Your name</label>
                <input
                  type="text"
                  id="name"
                  name="fullname"
                  value={formData.fullname}
                  onChange={handleChange}
                  placeholder="Enter your name"
                />
              </div>

              <div className="field">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div className="field">
              <label htmlFor="custom_message">How can we help?</label>
              <textarea
                id="custom_message"
                rows={6}
                name="custom_message"
                value={formData.custom_message}
                onChange={handleChange}
                placeholder="Tell us about your project, question, or custom idea"
              />
            </div>

            <section className="section-card">
              <div className="section-header">
                <h2>What are you interested in?</h2>
                <p>Select one or more options below.</p>
              </div>

              <div className="chips-grid">
                <label className="chip-option" htmlFor="products">
                  <input type="checkbox" id="products" name="requested_services" value="3dprints" />
                  <span>3D Prints</span>
                </label>

                <label className="chip-option" htmlFor="website">
                  <input type="checkbox" id="website" name="requested_services" value="website" />
                  <span>Website Development</span>
                </label>

                <label className="chip-option" htmlFor="cloud-consulting">
                  <input
                    type="checkbox"
                    id="cloud-consulting"
                    name="requested_services"
                    value="cloud-consulting"
                  />
                  <span>Cloud Consulting</span>
                </label>

                <label className="chip-option" htmlFor="development">
                  <input
                    type="checkbox"
                    id="development"
                    name="requested_services"
                    value="development"
                  />
                  <span>App or Mobile Development</span>
                </label>

                <label className="chip-option" htmlFor="consulting">
                  <input type="checkbox" id="consulting" name="requested_services" value="consulting" />
                  <span>IT Consulting</span>
                </label>

                <label className="chip-option" htmlFor="support">
                  <input type="checkbox" id="support" name="requested_services" value="support" />
                  <span>Support Request</span>
                </label>

                <label className="chip-option" htmlFor="ai_services">
                  <input type="checkbox" id="ai_services" name="requested_services" value="ai-services" />
                  <span>AI Chatbots, Agents, and Training</span>
                </label>

                <label className="chip-option" htmlFor="tutoring">
                  <input type="checkbox" id="tutoring" name="requested_services" value="tutoring" />
                  <span>Tutoring</span>
                </label>

                <label className="chip-option" htmlFor="other">
                  <input type="checkbox" id="other" name="requested_services" value="other" />
                  <span>Other</span>
                </label>
              </div>
            </section>

            <section className="section-card human-check-card">
              <div className="section-header">
                <h2>Quick human check</h2>
                <p>Type what is shown in the image.</p>
              </div>

              <div className="human-check-row">
                <div className="turnstile-wrap">
                  {turnstileSiteKey ? (
                    <Turnstile
                      ref={turnstileRef}
                      siteKey={turnstileSiteKey}
                      options={{
                        action: 'contact',
                        size: useCompactTurnstile ? 'compact' : 'flexible',
                        responseField: false,
                      }}
                      onSuccess={(token) => {
                        setTurnstileToken(token);
                        setBotStatus('');
                      }}
                      onExpire={() => {
                        setTurnstileToken('');
                        setBotStatus('Verification expired. Please verify again.');
                      }}
                      onError={() => {
                        setTurnstileToken('');
                        setBotStatus('Verification could not load. Please try again.');
                      }}
                    />
                  ) : (
                    <p className="turnstile-config-error">
                      Human verification is not configured.
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={!formReady}
                  className={formReady ? 'button_enabled' : 'button_disabled'}
                >
                  {isSubmitting ? 'Sending…' : 'Send Message'}
                </button>
              </div>

              <div className="status-row">
                <span className="botStatus" role="status" aria-live="polite">
                  {botStatus}
                </span>
              </div>
            </section>
          </form>
        </div>
      )}
    </div>
  );
};

export default Contact;
